import {
  AlertTriangle,
  CreditCard,
  ExternalLink,
  HardDrive,
  Loader2,
  PauseCircle,
  ShieldCheck,
  Timer,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { ProductKey } from '../../lib/app-registry';
import { daysRemaining } from '../../lib/billing';
import { formatBytes } from '../../lib/bytes';
import { trpc } from '../../lib/trpc';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { CancelPlanDialog } from './CancelPlanDialog';
import { TokenUsageCard } from './TokenUsageCard';

export type { ProductKey };

interface SubscriptionBillingPanelProps {
  organizationId: number;
  productKey: ProductKey;
  productLabel: string;
  isAdmin: boolean;
}

const TIER_LABEL: Record<string, string> = {
  free: 'Free',
  trialing: 'Team Trial',
  paid: 'Team',
  waived: 'Included access',
};

type BillingInterval = 'month' | 'year';

interface CatalogPrice {
  interval: string;
  intervalCount: number;
  unitAmountCents: number;
  currency: string;
}

/**
 * Everything is quoted per seat per month so the two intervals are comparable at a glance. An
 * annual price is a per-seat-per-year amount, so it is divided down rather than stored twice —
 * a second stored figure drifts from the one Stripe actually charges.
 */
function perSeatMonthlyCents(price: CatalogPrice): number {
  const months = price.interval === 'year' ? 12 * price.intervalCount : price.intervalCount;
  return price.unitAmountCents / Math.max(months, 1);
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/** Discount of the annual rate against the monthly one, or null when there is nothing to show. */
function annualSavingPercent(monthly?: CatalogPrice, annual?: CatalogPrice): number | null {
  if (!monthly || !annual) return null;
  const monthlyRate = perSeatMonthlyCents(monthly);
  if (monthlyRate <= 0) return null;
  const saving = Math.round((1 - perSeatMonthlyCents(annual) / monthlyRate) * 100);
  return saving > 0 ? saving : null;
}

/**
 * Per-product subscription/billing panel — each portal renders exactly one of these for its
 * own product (e.g. Clinic Portal passes productKey="inbox_clinic"). Seats are per-product,
 * not a shared pool, so this never shows another product's billing state.
 *
 * Seats are the billable *and* the access unit: an organization can hold any number of members,
 * and only seat holders can use the product. The panel therefore leads with seat usage against
 * the plan ceiling rather than with subscription status.
 */
export function SubscriptionBillingPanel({
  organizationId,
  productKey,
  productLabel,
  isAdmin,
}: SubscriptionBillingPanelProps) {
  const utils = trpc.useUtils();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month');

  const { data: billingState, isLoading: isBillingStateLoading } =
    trpc.workspace.getBillingState.useQuery({ productKey }, { enabled: isAdmin });

  const tier = billingState?.tier ?? 'free';
  const isWaived = tier === 'waived';
  const isPaid = tier === 'paid';
  const isTrialing = tier === 'trialing';
  // Distinct from `tier`, which collapses both a payment-failed org and a never-upgraded org to
  // 'free' — this is the only signal that tells the two apart.
  const isPastDue = billingState?.stripeStatus === 'past_due';

  // Only needed to price the upgrade, so it is not fetched for orgs that already pay.
  const { data: plans } = trpc.billing.listPlans.useQuery(
    { organizationId, productKey },
    { enabled: isAdmin && !!organizationId && !isPaid && !isWaived }
  );

  // The plan on offer: a bespoke one cut for this org wins, otherwise the catalog default.
  const offeredPlan =
    plans?.find((plan) => plan.isBespoke) ?? plans?.find((plan) => plan.isDefault);
  const monthlyPrice = offeredPlan?.prices.find((price) => price.interval === 'month');
  const annualPrice = offeredPlan?.prices.find((price) => price.interval === 'year');
  const selectedPrice = billingInterval === 'year' ? annualPrice : monthlyPrice;
  const annualSaving = annualSavingPercent(monthlyPrice, annualPrice);

  const { data: billingPortal, isLoading: isPortalLoading } = trpc.billing.getPortalUrl.useQuery(
    { organizationId, productKey },
    { enabled: isAdmin && !!organizationId && (isPaid || isPastDue) }
  );

  const createCheckoutMutation = trpc.billing.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.waived) {
        toast.info(`${productLabel} access is currently included by an administrator.`);
        utils.auth.me.invalidate();
        utils.workspace.getBillingState.invalidate();
      } else if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data.addedDirectly) {
        toast.success(`${productLabel} added to your subscription.`);
        utils.workspace.getBillingState.invalidate();
      } else if (data.paymentRequired) {
        toast.error(`Payment needs attention before ${productLabel} access can be activated.`);
        utils.workspace.getBillingState.invalidate();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start checkout.');
    },
  });

  const startTrialMutation = trpc.workspace.startExpansionTrial.useMutation({
    onSuccess: (data) => {
      toast.success(`Team Trial started — up to ${data.seats} seats for the next 14 days.`);
      utils.auth.me.invalidate();
      utils.workspace.getBillingState.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Could not start the Team Trial.');
    },
  });

  if (!isAdmin) {
    return null;
  }

  const seatsActive = billingState?.seatsActive ?? 0;
  const seatsPaused = billingState?.seatsPaused ?? 0;
  const seatCap = billingState?.seatCap ?? null;
  const freeSeats = billingState?.freeSeats ?? 2;
  const orgMemberCount = billingState?.orgMemberCount ?? 0;
  const trial = billingState?.trial;
  const storage = billingState?.storage;
  const atSeatCap = seatCap !== null && seatsActive >= seatCap;
  const trialDaysLeft = daysRemaining(trial?.expiresAt ?? null);

  return (
    <>
      {isPastDue && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Payment failed — reverted to the Free plan</AlertTitle>
          <AlertDescription>
            <p>
              Your last payment for {productLabel} failed, so this organization reverted to the Free
              plan.{' '}
              {seatsPaused > 0
                ? `${seatsPaused} seat${seatsPaused === 1 ? ' is' : 's are'} paused as a result.`
                : null}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              disabled={isPortalLoading || !billingPortal?.portalUrl}
              onClick={() => {
                if (billingPortal?.portalUrl) {
                  window.open(billingPortal.portalUrl, '_blank');
                } else {
                  toast.error('Billing portal is not available right now.');
                }
              }}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Update payment method
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
        <CardHeader className="border-b border-border/50 bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              <CardTitle>{productLabel} Plan</CardTitle>
            </div>
            <Badge variant={isPaid || isWaived ? 'default' : 'secondary'}>
              {TIER_LABEL[tier] ?? 'Free'}
            </Badge>
          </div>
          <CardDescription>
            {productLabel} is billed per assigned seat. Organization membership is always free —
            only teammates you give a seat are included in the subscription.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
            <div className="space-y-1">
              <h3 className="font-semibold text-lg">
                {isWaived
                  ? `${productLabel} Access Included`
                  : isPaid
                    ? `${productLabel} Team Plan`
                    : isTrialing
                      ? `Team Trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`
                      : `${productLabel} Free Plan`}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {isWaived
                  ? `An administrator has included ${productLabel} access for this organization. No payment method is required.`
                  : isPaid
                    ? `Seats update your subscription automatically as you assign and remove them under Members.`
                    : isTrialing
                      ? `You can use up to ${trial?.seats ?? 5} seats until the trial ends. Subscribe before then to keep them.`
                      : `Includes ${seatCap ?? 2} seats at no cost. Subscribe when your team needs more.`}
              </p>
            </div>

            <div className="shrink-0 flex flex-col gap-3 w-full md:w-auto md:items-end">
              {!isPaid && !isWaived && annualPrice && (
                <div className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-muted/40 p-1">
                  {(['month', 'year'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setBillingInterval(option)}
                      className={
                        billingInterval === option
                          ? 'rounded-md bg-background px-3 py-1 text-xs font-semibold shadow-sm'
                          : 'rounded-md px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors'
                      }
                    >
                      {option === 'month' ? 'Monthly' : 'Annual'}
                      {option === 'year' && annualSaving !== null ? ` · save ${annualSaving}%` : ''}
                    </button>
                  ))}
                </div>
              )}

              {!isPaid && !isWaived && selectedPrice && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">
                    {formatMoney(perSeatMonthlyCents(selectedPrice), selectedPrice.currency)}
                  </span>{' '}
                  per seat / month + GST
                  {billingInterval === 'year' ? ', billed annually' : ''}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                {trial?.available && (
                  <Button
                    variant="outline"
                    className="w-full md:w-auto"
                    disabled={startTrialMutation.isPending}
                    onClick={() => startTrialMutation.mutate({ productKey })}
                  >
                    {startTrialMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Timer className="mr-2 h-4 w-4" />
                    )}
                    Start {trial.days}-day Team Trial
                  </Button>
                )}

                {!isPaid && !isWaived && (
                  <Button
                    className="w-full md:w-auto shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95 group"
                    disabled={createCheckoutMutation.isPending}
                    onClick={() =>
                      createCheckoutMutation.mutate({
                        organizationId,
                        productKey,
                        productPlanId: offeredPlan?.id,
                        interval: billingInterval,
                      })
                    }
                  >
                    {createCheckoutMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Zap className="mr-2 h-4 w-4 group-hover:fill-primary-foreground/20 transition-colors" />
                    )}
                    Subscribe to {productLabel}
                  </Button>
                )}

                {isPaid && (
                  <Button
                    variant="outline"
                    className="w-full md:w-auto hover:bg-muted transition-colors"
                    disabled={isPortalLoading || !billingPortal?.portalUrl}
                    onClick={() => {
                      if (billingPortal?.portalUrl) {
                        window.open(billingPortal.portalUrl, '_blank');
                      } else {
                        toast.error('Billing portal is not available right now.');
                      }
                    }}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Manage Billing
                  </Button>
                )}

                {isPaid && (
                  <CancelPlanDialog
                    organizationId={organizationId}
                    productKey={productKey}
                    productLabel={productLabel}
                  />
                )}
              </div>
            </div>
          </div>

          {!isBillingStateLoading && billingState && (
            <div className="border-t border-border/50 mt-6 pt-6 space-y-4">
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Seats
                  </span>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-foreground via-foreground/90 to-muted-foreground bg-clip-text text-transparent">
                      {seatsActive}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {seatCap === null ? 'assigned' : `of ${seatCap} used`} &middot;{' '}
                      {orgMemberCount} member{orgMemberCount === 1 ? '' : 's'} in the org
                    </span>
                  </div>
                </div>

                {storage && (
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Attachment storage
                    </span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-foreground via-foreground/90 to-muted-foreground bg-clip-text text-transparent">
                        {formatBytes(storage.usedBytes)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        of {formatBytes(storage.limitBytes)} pooled
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className="bg-emerald-500/5 text-emerald-500 border border-emerald-500/10 gap-1"
                >
                  <ShieldCheck className="h-3 w-3 text-emerald-500" /> {TIER_LABEL[tier] ?? 'Free'}
                </Badge>

                {seatsPaused > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <PauseCircle className="h-3 w-3" /> {seatsPaused} seat
                    {seatsPaused === 1 ? '' : 's'} paused
                  </Badge>
                )}

                {storage && (
                  <Badge variant="outline" className="gap-1">
                    <HardDrive className="h-3 w-3" /> {formatBytes(storage.maxFileBytes)} max file
                    size
                  </Badge>
                )}
              </div>

              <p className="text-xs text-muted-foreground/85 leading-relaxed max-w-2xl">
                {seatsPaused > 0
                  ? `${seatsPaused} assigned seat${seatsPaused === 1 ? ' is' : 's are'} paused because your organization has more seats assigned than the ${TIER_LABEL[tier] ?? 'Free'} plan allows. Nothing has been deleted — subscribe, or free a seat under Members, and they come straight back.`
                  : isWaived
                    ? `Assign or remove seats under Members without affecting Stripe billing. An administrator manages this included access.`
                    : isPaid
                      ? `Assign or remove seats under Members — your ${productLabel} subscription quantity follows automatically.`
                      : isTrialing
                        ? `When the trial ends, your first ${freeSeats} seats and all your data stay put; any extra seats pause until you subscribe.`
                        : atSeatCap
                          ? `You are using all ${seatCap} free seats. ${trial?.available ? `Start the ${trial.days}-day Team Trial to try up to ${trial.seats} seats, no card required.` : `Subscribe to ${productLabel} to add more.`}`
                          : `Invite as many teammates as you like — only the ones you give a seat can use ${productLabel}.`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      <TokenUsageCard isAdmin={isAdmin} />
    </>
  );
}
